import type { ChangeEvent, KeyboardEvent, PropsWithChildren } from "react";
import { css, styled } from "styled-components";

import { CheckIcon } from "common/components";
import { logger } from "common/utilities/logger";

const StyledIcon = styled(CheckIcon)`
  position: absolute;
  display: none;
  bottom: 14px;
  right: 14px;
  width: 12px;
  height: 12px;
  color: ${({ theme }) => theme.colors.checkboxIcon};
`;

const StyledInput = styled.input`
  appearance: none;
  width: 40px;
  height: 40px;
  margin: 0;
  &:checked + ${StyledIcon} {
    display: block;
  }
`;

const CheckboxBackground = styled.div<{ checked: boolean }>`
  visibility: visible;
  content: "";
  width: 36px;
  height: 36px;
  border-color: ${({ theme }) => theme.colors.checkboxBorder};
  border-style: solid;
  border-width: 2px;
  border-radius: 20px;
  position: absolute;
  bottom: 0;
  right: 0;

  ${({ checked }) =>
    checked &&
    css`
      background-color: ${({ theme }) => theme.colors.checkboxBackground};
    `};
`;

const StyledText = styled.span`
  padding-left: 20px;
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
`;

type CheckboxProps = PropsWithChildren & {
  className?: string;
  id: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  text: string;
  value: boolean;
};

export const Checkbox = ({ className, id, onChange, text, value }: CheckboxProps) => {
  const handleKeyDown = (event: KeyboardEvent) => {
    try {
      if (event.code === "Enter") onChange({ target: { name: id } } as ChangeEvent<HTMLInputElement>);
    } catch (error) {
      logger.errorLocal("[Checkbox] handleKeyDown encountered an error", error);
    }
  };

  return (
    <StyledLabel className={className} htmlFor={id}>
      <StyledText>{text}</StyledText>
      <CheckboxBackground checked={value} />
      <StyledInput
        tabIndex={0}
        type="checkbox"
        id={id}
        name={id}
        checked={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
      />
      <StyledIcon />
    </StyledLabel>
  );
};
